"use client";

import React, { useMemo, useState, Suspense } from "react";
import { Eye, EyeOff, Lock, Mail, Shield } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import { createBrowserClient } from "@/lib/supabase/client";
import { AuthShell } from "@/components/auth/AuthShell";
import { AuthPanel } from "@/components/auth/AuthPanel";
import { AuthField } from "@/components/auth/AuthField";
import { PrimaryButton, SocialButton, Divider } from "@/components/auth/AuthButtons";
import { StrengthBar, passwordScore } from "@/components/auth/StrengthBar";
import { OpsPulseLoader } from "@/components/ui/OpsPulseLoader";
import Link from "next/link";

function cn(...classes: Array<string | false | undefined | null>) {
  return classes.filter(Boolean).join(" ");
}

function SignupContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const inviteToken =
    searchParams?.get("inviteToken") ||
    searchParams?.get("token") ||
    "";
  const inviteEmail = searchParams?.get("email") || "";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [banner, setBanner] = useState<null | { type: "ok" | "err"; msg: string }>(
    null
  );

  React.useEffect(() => {
    if (inviteEmail && !email) {
      setEmail(inviteEmail);
    }
  }, [inviteEmail, email]);

  const validEmail = useMemo(
    () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()),
    [email]
  );
  const pwScore = useMemo(() => passwordScore(password), [password]);
  const canSubmit = validEmail && pwScore >= 2 && !!inviteToken;

  const onSubmit = async () => {
    setBanner(null);
    if (!canSubmit) {
      setBanner({ type: "err", msg: "Please complete all fields correctly." });
      return;
    }
    setLoading(true);

    try {
      if (!inviteToken) {
        throw new Error("Invite required. Please use your invite link to sign up.");
      }

      const supabase = createBrowserClient();
      if (!supabase) {
        throw new Error("Supabase client not configured");
      }

      // Step 1: Create Supabase auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password: password,
      });

      if (authError) {
        throw new Error(authError.message || "Failed to create account");
      }

      if (!authData.user) {
        throw new Error("User creation failed");
      }

      // Step 2: Create tenant using API
      const response = await fetch("/api/auth/signup", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId: authData.user.id,
          email: email.trim().toLowerCase(),
          inviteToken,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to create tenant");
      }

      // Step 3: Redirect to tenant dashboard or select-tenant
      router.push(result.redirectPath || "/select-tenant");
    } catch (error: any) {
      console.error("Signup error:", error);
      setBanner({
        type: "err",
        msg: error.message || "Failed to create account. Please try again.",
      });
      setLoading(false);
    }
  };

  return (
    <AuthPanel>
        {/* Tabs - Switch between Create Account and Sign In */}
        <div className="mb-6 flex gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-1">
          <Link
            href="/auth/signup"
            className={cn(
              "flex-1 rounded-lg px-4 py-2 text-center text-sm font-medium transition",
              "bg-emerald-500/10 text-emerald-300 ring-1 ring-emerald-400/20"
            )}
          >
            Create account
          </Link>
          <Link
            href="/auth/login"
            className={cn(
              "flex-1 rounded-lg px-4 py-2 text-center text-sm font-medium transition",
              "text-white/60 hover:bg-white/[0.06] hover:text-white/80"
            )}
          >
            Sign in
          </Link>
        </div>

        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-lg font-semibold tracking-tight">Create your account</div>
            <div className="mt-1 text-sm text-white/60">
              Sign up with your invitation link.
            </div>
          </div>
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-500/10 ring-1 ring-emerald-400/15">
            <Shield className="h-5 w-5 text-emerald-300" />
          </div>
        </div>

        {/* Banner */}
        {banner ? (
          <div
            className={cn(
              "mt-5 rounded-xl border px-3 py-2 text-sm",
              banner.type === "ok"
                ? "border-emerald-400/20 bg-emerald-500/10 text-emerald-200"
                : "border-amber-400/20 bg-amber-500/10 text-amber-200"
            )}
          >
            {banner.msg}
          </div>
        ) : null}

        {/* Form */}
        <div className="mt-6 grid gap-4">
          {!inviteToken ? (
            <div className="rounded-xl border border-amber-400/20 bg-amber-500/10 p-3 text-sm text-amber-200">
              Invite required. Please use your invite link to sign up.
              <div className="mt-2">
                <Link
                  href="/auth/accept-invite"
                  className="text-amber-100 underline decoration-amber-200/40 underline-offset-4"
                >
                  Enter invite link
                </Link>
              </div>
            </div>
          ) : null}

          <AuthField
            label="Email address"
            placeholder="name@company.com"
            value={email}
            onChange={setEmail}
            type="email"
            autoComplete="email"
            icon={<Mail className="h-4 w-4" />}
            disabled={!!inviteEmail}
          />

          <div>
            <AuthField
              label="Password"
              placeholder="Create a password"
              value={password}
              onChange={setPassword}
              type={showPw ? "text" : "password"}
              autoComplete="new-password"
              icon={<Lock className="h-4 w-4" />}
              right={
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  className="grid h-9 w-9 place-items-center rounded-lg text-white/55 hover:bg-white/[0.06] hover:text-white transition"
                  aria-label={showPw ? "Hide password" : "Show password"}
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              }
            />
            <StrengthBar value={pwScore} />
            <div className="mt-2 text-xs text-white/45">
              Use 8+ chars with a number and a symbol for best security.
            </div>
          </div>

          <PrimaryButton disabled={!canSubmit} loading={loading} onClick={onSubmit}>
            Create account
          </PrimaryButton>

          <Divider text="or" />

          <SocialButton provider="google" onClick={() => setBanner({ type: "ok", msg: "Google SSO coming soon." })} />
          <SocialButton provider="microsoft" onClick={() => setBanner({ type: "ok", msg: "Microsoft SSO coming soon." })} />
        </div>

        {/* Already have account section */}
        <div className="mt-6 flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
          <div className="text-sm text-white/70">
            Already have an account?
          </div>
          <Link
            href="/auth/login"
            className={cn(
              "rounded-lg px-4 py-2 text-sm font-semibold",
              "bg-emerald-500/10 text-emerald-300 ring-1 ring-emerald-400/20",
              "hover:bg-emerald-500/15 hover:text-emerald-200 transition"
            )}
          >
            Sign in
          </Link>
        </div>

        {/* Info box */}
        <div className="mt-6 rounded-xl border border-white/10 bg-white/[0.03] p-3">
          <div className="text-xs font-medium text-white/70">On signup we will:</div>
          <ul className="mt-2 space-y-1 text-xs text-white/55">
            <li>• Create your user account</li>
            <li>• Attach you to your invited tenant</li>
            <li>• Apply your invited role</li>
            <li>• Send you to your dashboard</li>
          </ul>
        </div>
    </AuthPanel>
  );
}

export default function SignupPage() {
  return (
    <AuthShell>
      <Suspense
        fallback={
          <AuthPanel>
            <div className="text-center py-8">
              <OpsPulseLoader size={48} label="Loading signup..." />
            </div>
          </AuthPanel>
        }
      >
        <SignupContent />
      </Suspense>
    </AuthShell>
  );
}
